import React, { lazy, ReactElement } from 'react';

import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router';

import { BRAND_APPROVAL_STATUSES } from '@/config/common';
import { ROLES } from '@/config/user';

import { IServicePartner, IUserInfo } from '@/pages/Dashboard/types';
import { SharedRouteProps } from '../types';

const NotAllowed = lazy(() => import(/* webpackChunkName: "NotAllowed" */ '@/pages/NotAllowed'));

type PermissionsGuardProps = SharedRouteProps & {
  children: ReactElement;
  userInfo: IUserInfo;
  servicePartner: IServicePartner;
};

export function PermissionsGuard({
  children,
  userInfo,
  servicePartner,
  subscribedUser = false,
  efficiencyPartner = false,
  eligibleCountries = [],
  eligibleBrands = [],
  requiredRoles = [],
}: PermissionsGuardProps) {
  const location = useLocation();

  const subsPayment = userInfo?.recurringPayment;

  const userHasOrHadSubscriptions = () => subsPayment?.subscription !== null;
  const userHasValidPaidSubscription = () => subsPayment?.isValid && !subsPayment?.isPaused;

  const userHasPermissions = () => requiredRoles.some(role => userInfo?.spRoles?.includes(role));

  const spHasEligibleCountry = () => {
    if (eligibleCountries.length > 0) return eligibleCountries.includes(servicePartner?.address?.country?.name);

    return true;
  };

  const spHasEligibleBrand = () => {
    if (eligibleBrands.length > 0) {
      const [mainBrand] = servicePartner?.brands || [];
      const mainBrandId = String(mainBrand?.brandId || '').toLowerCase();
      return mainBrand && eligibleBrands.includes(mainBrandId);
    }

    return true;
  };

  const spIsEfficiencyPartner = () => servicePartner?.isEfficiencyPartner === true;

  const spHasBrandApprovals = () =>
    !servicePartner?.brands?.some(brand => brand.approvalStatus !== BRAND_APPROVAL_STATUSES.APPROVED);

  // Synchronous role check — runs on every render so there's no one-frame flash of
  // children before useEffect fires when userInfo is already available (e.g. back-navigation).
  const roleBlocked = userInfo != null && requiredRoles.length > 0 && !userHasPermissions();

  if (roleBlocked) {
    const isGuest = userInfo?.spRoles?.includes(ROLES.guest);
    if (isGuest) return <Navigate to="/new-service-partner" replace />;
    return <NotAllowed />;
  }

  // SP restrictions computed synchronously so stale state can never persist across route changes.
  if (userInfo && servicePartner) {
    // TODO: Fire INFO notification that user has to get brand approval first
    if (!spHasBrandApprovals()) return <NotAllowed />;

    // TODO: Fire INFO notification that user has to activate trial first
    if (!userHasOrHadSubscriptions() && location.pathname === '/redeem-voucher') return <NotAllowed />;

    // TODO: Fire INFO notification that user has to buy subscriptions first
    if (subscribedUser && !userHasValidPaidSubscription()) {
      if (location.pathname === '/systems') return <Navigate to="/no-subscription" replace />;
      return <NotAllowed />;
    }

    // TODO: Fire INFO notification that SP is not efficiencyPartner
    if (efficiencyPartner && !spIsEfficiencyPartner()) return <NotAllowed />;

    // TODO: Fire INFO notification that SP country is not eligible
    if (!spHasEligibleCountry()) return <NotAllowed />;

    // TODO: Fire INFO notification that SP brand is not eligible
    if (!spHasEligibleBrand()) return <NotAllowed />;
  }

  return children;
}

const stateProps = ({ app: { userInfo, servicePartner } }) => ({
  userInfo,
  servicePartner,
});

export default connect(stateProps)(PermissionsGuard);
